import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { Collection, CollectionProduct } from '../models/collections';

@Injectable({
  providedIn: 'root',
})
export class CollectionService {
  private readonly collections$ = new BehaviorSubject<Collection[]>([]);
  private readonly http: HttpClient = inject(HttpClient);
  private initiated = false;

  initCollections(): void {
    if (this.initiated) return;
    this.http.get<Collection[]>(environment.apiPrefix + environment.collections).subscribe({
      next: (cols) => this.collections$.next(cols),
      error: (err) => {
        console.error('Failed to load collections', err);
      },
      complete: () => (this.initiated = true),
    });
  }

  getCollections(): Observable<Collection[]> {
    return this.collections$.asObservable();
  }

  createCollectionWithProduct(collectionName: string, product: CollectionProduct): Observable<Collection> {
    const payload = { name: collectionName };

    return this.http.post<Collection>(environment.apiPrefix + environment.collections, payload).pipe(
      tap((newCollection) => {
        const currentCollections = this.collections$.getValue();
        this.collections$.next([...currentCollections, newCollection]);
        this.addProductToCollection(newCollection.id, product).subscribe();
      }),
      catchError((error) => {
        console.error('Create collection failed', error);
        return throwError(() => error);
      }),
    );
  }

  createCollection(collectionName: string): Observable<Collection> {
    const payload = { name: collectionName };

    return this.http.post<Collection>(environment.apiPrefix + environment.collections, payload).pipe(
      tap((newCollection) => {
        const currentCollections = this.collections$.getValue();
        this.collections$.next([...currentCollections, newCollection]);
      }),
      catchError((error) => {
        console.error('Create collection failed', error);
        return throwError(() => error);
      }),
    );
  }

  deleteCollection(collectionId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiPrefix}${environment.collections}/${collectionId}`).pipe(
      tap(() => {
        const currentCollections = this.collections$.getValue();
        const updatedCollections = currentCollections.filter((col) => col.id !== collectionId);
        this.collections$.next(updatedCollections);
      }),
      catchError((error) => {
        console.error('Delete collection failed', error);
        return throwError(() => error);
      }),
    );
  }

  addProductToCollection(collectionId: string, product: CollectionProduct): Observable<void> {
    const payload = { product_ids: [product.id] };

    return this.http
      .post<void>(`${environment.apiPrefix}${environment.collections}/${collectionId}/add-products`, payload)
      .pipe(
        tap(() => {
          const currentCollections = this.collections$.getValue();
          const collectionToUpdate = currentCollections.find((col) => col.id === collectionId);
          if (collectionToUpdate) {
            if (!collectionToUpdate.products) {
              collectionToUpdate.products = [];
            }
            collectionToUpdate.products.push(product);
            this.collections$.next([...currentCollections]);
          }
        }),
        catchError((error) => {
          console.error('Add product to collection failed', error);
          return throwError(() => error);
        }),
      );
  }

  removeProductFromCollection(collectionId: string, productId: number): Observable<void> {
    return this.http
      .delete<void>(`${environment.apiPrefix}${environment.collections}/${collectionId}/products/${productId}`)
      .pipe(
        tap(() => {
          const currentCollections = this.collections$.getValue();

          const updatedCollections = currentCollections.map((col) => {
            if (col.id === collectionId) {
              return {
                ...col,
                products: col.products?.filter((product) => product.id !== productId) ?? [], // Create a new filtered array
              };
            }
            return col;
          });

          this.collections$.next(updatedCollections);
        }),
        catchError((error) => {
          console.error('Remove product from collection failed', error);
          return throwError(() => error);
        }),
      );
  }

  removeProductsFromCollection(collectionId: string, productIds: number[]): Observable<void> {
    const payload = { products: productIds };

    return this.http.patch<void>(`${environment.apiPrefix}${environment.collections}/${collectionId}`, payload).pipe(
      tap(() => {
        const currentCollections = this.collections$.getValue();
        const collectionIndex = currentCollections.findIndex((col) => col.id === collectionId);
        if (collectionIndex !== -1) {
          const allProducts = currentCollections[collectionIndex].products;
          const productsToKeep = allProducts.filter((p) => productIds.includes(p.id));

          const updatedCollections = [...currentCollections];
          updatedCollections[collectionIndex] = {
            ...updatedCollections[collectionIndex],
            products: productsToKeep,
          };
          this.collections$.next(updatedCollections);
        }
      }),
      catchError((error) => {
        console.error('Remove products from collection failed', error);
        return throwError(() => error);
      }),
    );
  }

  updateCollection(collectionId: string, updatedName: string, products: CollectionProduct[]): Observable<Collection> {
    const productIds = products.map((p) => p.id);
    const payload = {
      name: updatedName,
      productIds: productIds,
    };

    return this.http
      .patch<Collection>(`${environment.apiPrefix}${environment.collections}/${collectionId}`, payload)
      .pipe(
        tap((updatedCollection) => {
          const currentCollections = this.collections$.getValue();
          const collectionIndex = currentCollections.findIndex((col) => col.id === updatedCollection.id);
          if (collectionIndex !== -1) {
            const newCollections = [...currentCollections];
            newCollections[collectionIndex] = updatedCollection;
            this.collections$.next(newCollections);
          }
        }),
        catchError((error) => {
          console.error('Update collection failed', error);
          return throwError(() => error);
        }),
      );
  }
}
